import { bloc, documentHtml, type MarqueDeDocument, type Valeur } from '@taki/pdf';
import type { TakiSdk } from '@taki/sdk';
import { cellToText, projectRecord } from '@taki/ui';
import type { CarteAssemblee, ViewBinding } from '@taki/ui';

/**
 * LA PARTIE PURE DES GESTES DE FICHE (`gestes-de-fiche.ts`) : rendre une fiche
 * en HTML, suivre l'export d'une fiche. Sans React ni navigateur : le test
 * (`tests/gestes-de-fiche.test.mjs`) la tient telle quelle.
 */

/* ------------------------------------------------------------------ */
/* Le suivi des exports, par fiche                                     */
/* ------------------------------------------------------------------ */

export type EtatDExport =
  | { etat: 'enCours' }
  | { etat: 'fait'; nom: string; avertissement?: string }
  | { etat: 'echec'; message: string };

const etats = new Map<string, EtatDExport>();
const abonnes = new Set<() => void>();

/** La clef d'une fiche dans le suivi. */
export function cleDExport(collection: string, id: string): string {
  return `${collection}/${id}`;
}

export const suiviDesExports = {
  lire: (cle: string): EtatDExport | undefined => etats.get(cle),
  poser(cle: string, etat: EtatDExport | null): void {
    if (etat === null) etats.delete(cle);
    else etats.set(cle, etat);
    for (const abonne of abonnes) abonne();
  },
  abonner(abonne: () => void): () => void {
    abonnes.add(abonne);
    return () => abonnes.delete(abonne);
  },
};

/* ------------------------------------------------------------------ */
/* La fiche en HTML                                                    */
/* ------------------------------------------------------------------ */

const AUJOURDHUI = new Intl.DateTimeFormat('fr-LU', { dateStyle: 'long' });

/**
 * LE TEXTE QUE L'ECRAN MONTRE, dans l'ordre de ce qui est le plus juste : le
 * libelle (catalogue, reference), puis le rendu de la colonne quand il est du
 * texte (les habillages du socle : « 18 500 € », « 18/04/2027 »), puis la
 * valeur brute. Un rendu React (une pastille) n'est pas du texte : on retombe
 * alors sur la valeur.
 */
function texteDeCellule(cellule: Parameters<typeof cellToText>[0]): Valeur {
  if (cellule === undefined || cellule === null || typeof cellule !== 'object' || !('value' in cellule)) return cellToText(cellule);
  const { value, node, label } = cellule as { value: unknown; node?: unknown; label?: unknown };
  if (typeof label === 'string' && label !== '') return label;
  /* UNE DATE SANS HABILLAGE se rend a la locale du POSTE a l'ecran ; le
     document, lui, est envoye : il parle la langue du produit. */
  if (value instanceof Date && (typeof node !== 'string' || node === cellToText(value as Date))) {
    return value.toLocaleDateString('fr-LU');
  }
  if (typeof node === 'string' && node !== '') return node;
  if (typeof node === 'number') return String(node);
  return cellToText(cellule);
}

export interface FicheARendre<T> {
  marque: MarqueDeDocument;
  /** Le nom de la porte (« Materiel ») : la ligne sous le titre. */
  porte: string;
  binding: ViewBinding<T>;
  record: T;
  id: string;
  maintenant?: Date;
}

/**
 * LA FICHE EN DOCUMENT : son titre, puis une section par carte de champs, dans
 * l'ordre de la vue `core.fiche`. Les cartes liees et la carte des fichiers ne
 * s'impriment pas (ce sont d'autres fiches, et des binaires). Une fiche sans
 * carte declaree imprime toutes ses colonnes, sous « Fiche ».
 *
 * Fonction PURE : le test la tient sans navigateur.
 */
export function ficheEnHtml<T>(fiche: FicheARendre<T>): { nom: string; html: string } {
  const { binding, record, id } = fiche;
  const ligne = projectRecord(record, id, binding);
  const colonnes = binding.columns ?? [];
  const libelle = (cle: string): string => colonnes.find((colonne) => colonne.key === cle)?.label ?? cle;
  const cartes = ((binding.options?.['cards'] ?? []) as CarteAssemblee<T>[]).filter(
    (carte): carte is Extract<CarteAssemblee<T>, { kind: 'fields' }> => carte.kind === 'fields',
  );
  const sections = cartes.length > 0
    ? cartes.map((carte) => ({
        titre: carte.title,
        lignes: carte.keys
          .filter((cle): cle is string => typeof cle === 'string')
          .map((cle) => [libelle(cle), texteDeCellule(ligne.cells[cle])] as const),
      }))
    : [{ titre: 'Fiche', lignes: colonnes.map((colonne) => [colonne.label, texteDeCellule(ligne.cells[colonne.key])] as const) }];
  const jour = fiche.maintenant ?? new Date();
  const html = documentHtml({
    marque: fiche.marque,
    titre: ligne.title,
    sousTitre: `${fiche.porte} · ${AUJOURDHUI.format(jour)}`,
    corps: sections.filter((section) => section.lignes.length > 0).map((section) => bloc(section.titre, section.lignes)).join(''),
  });
  const date = `${jour.getFullYear()}-${String(jour.getMonth() + 1).padStart(2, '0')}-${String(jour.getDate()).padStart(2, '0')}`;
  return { nom: nomDeFichierPdf(`${ligne.title} ${date}`), html };
}

/* ------------------------------------------------------------------ */
/* L'export                                                            */
/* ------------------------------------------------------------------ */

export const RAISON_PDF = {
  fichiersFermes: 'Les fichiers joints ne sont pas activés pour ce jeu de données.',
  pdfRefuse: 'Ce jeu de données n’accepte pas les PDF.',
  enCours: 'Un PDF de cette fiche est en cours.',
} as const;

/**
 * UN NOM DE FICHIER LISIBLE PARTOUT : titre de la fiche et date, sans les
 * signes que Windows, macOS ou un navigateur refusent (`\ / : * ? " < > |`,
 * caracteres de controle), espaces resserres, `.pdf` au bout. Une fiche sans
 * titre utilisable devient « Fiche ».
 */
export function nomDeFichierPdf(brut: string): string {
  const propre = brut
    .replace(/[\\/:*?"<>|\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s.]+|[\s.]+$/g, '')
    .slice(0, 200)
    .trim();
  const base = propre.replace(/\.pdf$/i, '') || 'Fiche';
  return `${base}.pdf`;
}

/** Ce qui pose un fichier sur le poste ; le navigateur par defaut, un double dans les tests. */
export type Telecharger = (octets: Uint8Array, nom: string) => void;

/** Le telechargement du navigateur : un lien `download` sur un Blob, clique puis retire. */
export const telechargerDansLeNavigateur: Telecharger = (octets, nom) => {
  const url = URL.createObjectURL(new Blob([new Uint8Array(octets)], { type: 'application/pdf' }));
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.append(lien);
  lien.click();
  lien.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/**
 * Lance l'export et tient le suivi a jour ; ne leve jamais, l'echec est dans le
 * suivi. DEUX RESULTATS, et le second ne defait pas le premier : le PDF est
 * RANGE dans les pieces jointes (l'historique), puis TELECHARGE sur le poste
 * sous un nom lisible. Un telechargement rate (connexion coupee, navigateur qui
 * bloque) laisse le PDF range : l'avis le dit, sans parler d'echec.
 */
export async function exporterEnPdf(
  sdk: TakiSdk,
  parent: { collection: string; id: string },
  document: { nom: string; html: string },
  telecharger: Telecharger = telechargerDansLeNavigateur,
): Promise<void> {
  const cle = cleDExport(parent.collection, parent.id);
  suiviDesExports.poser(cle, { etat: 'enCours' });
  let fichier;
  try {
    fichier = await sdk.files.genererPdf({ parent, nom: document.nom, html: document.html });
  } catch (cause) {
    suiviDesExports.poser(cle, {
      etat: 'echec',
      message: cause instanceof Error && cause.message !== '' ? cause.message : 'Le PDF n’a pas pu être produit.',
    });
    return;
  }
  try {
    telecharger(await sdk.files.download(fichier.id), nomDeFichierPdf(document.nom));
    suiviDesExports.poser(cle, { etat: 'fait', nom: fichier.name });
  } catch (cause) {
    suiviDesExports.poser(cle, {
      etat: 'fait',
      nom: fichier.name,
      avertissement: `Téléchargement impossible${cause instanceof Error && cause.message !== '' ? ` : ${cause.message}` : ''}. Le PDF est dans les pièces jointes.`,
    });
  }
}
